import {inject, Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {CompaniesService} from '../../../services/companies.service';
import {HttpClient} from '@angular/common/http';
import {IMeetingNote, MeetingNoteStatus} from '../models/IMeetingNote.interface';
import {BehaviorSubject, Observable} from 'rxjs';
import {IMeetingNotesQuery} from '../models/IMeetingNotesQuery.interface';
import {tap} from 'rxjs/operators';
import {IReaction} from '../models/IReaction.interface';

@Injectable({
  providedIn: 'root'
})
export class MeetingNotesService {

  private readonly _url = environment.apiConfig.corporateAPI.endpoint;
  private httpClient = inject(HttpClient);
  private readonly companyService = inject(CompaniesService);

  private get companyId(): number {
    return this.companyService.managedCompanyId;
  }

  private meetingNotes$ = new BehaviorSubject<IMeetingNote[]>([]);

  getMeetingNotesCached(): Observable<IMeetingNote[]> {
    return this.meetingNotes$.asObservable();
  }

  getMeetingNotes(formId: number, pageNumber: number, pageSize: number): Observable<IMeetingNotesQuery> {
    return this.httpClient.post<IMeetingNotesQuery>(
      this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/query`, {
        pageSize: pageSize,
        pageNumber: pageNumber
      }).pipe(
        tap(res => this.meetingNotes$.next(res.items))
    );
  }

  getNoteById(formId: number, meetingNoteId: number): Observable<IMeetingNote> {
    return this.httpClient.get<IMeetingNote>(this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${meetingNoteId}`);
  }

  editNote(formId: number, note: IMeetingNote): Observable<void> {
    return this.httpClient.put<void>(this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${note.id}`, note);
  }

  createNote(formId: number, note: IMeetingNote): Observable<number> {
    return this.httpClient.post<number>(this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes`, note);
  }

  deleteNote(formId: number, noteId: number): Observable<void> {
    return this.httpClient.delete<void>(this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}`);
  }

  updateNoteStatus(formId: number, noteId: number, status: MeetingNoteStatus): Observable<void> {
    return this.httpClient.patch<void>(
      this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/status`,
      { status }
    );
  }

  // Files

  addFiles(formId: number, noteId: number , files: File[]): Observable<number[]> {
    const formData = new FormData();
    files.map((file: File) => formData.append('files', file));
    return this.httpClient.post<number[]>(this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/files`, formData);
  }

  deleteFile(formId: number, noteId: number , fileId: number): Observable<void> {
    return this.httpClient.delete<void>(this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/files/${fileId}`);
  }

  // Reactions

  addReaction(formId: number, noteId: number, request: {
    emoji: string;
    noteOwnerAzureObjectId: string;
    clientName?: string;
    noteTitle?: string;
    rootMeetingNoteId?: number;
  }): Observable<IReaction> {
    return this.httpClient.post<IReaction>(
      this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/reactions`,
      request
    );
  }

  removeReaction(formId: number, noteId: number, reactionId: number): Observable<void> {
    return this.httpClient.delete<void>(
      this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/reactions/${reactionId}`
    );
  }

  getReactions(formId: number, noteId: number): Observable<IReaction[]> {
    return this.httpClient.get<IReaction[]>(
      this._url + `/company/${this.companyId}/forms/${formId}/meeting-notes/${noteId}/reactions`
    );
  }
}
